import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  NavigateBefore,
  NavigateNext,
  Home,
} from '@mui/icons-material';
import { marked } from 'marked';

// Guide metadata
interface GuideMetadata {
  id: string;
  title: string;
  filename: string;
  duration: string;
  level: string;
  description: string;
  order: number;
}

const guides: GuideMetadata[] = [
  {
    id: 'getting-started',
    title: 'Getting Started Guide',
    filename: '01-getting-started.md',
    description: 'Create your account, organization, and upload your first content',
    duration: '10 min read',
    level: 'Beginner',
    order: 1,
  },
  {
    id: 'authentication-security',
    title: 'Authentication & Security',
    filename: '02-authentication-security.md',
    description: 'Set up 2FA, manage OAuth providers, and secure your account',
    duration: '15 min read',
    level: 'All Levels',
    order: 2,
  },
  {
    id: 'content-upload-management',
    title: 'Content Upload & Management',
    filename: '03-content-upload-management.md',
    description: 'Master uploading, organizing, managing files, thumbnails, and version history',
    duration: '25 min read',
    level: 'Beginner',
    order: 3,
  },
  {
    id: 'organizations-teams',
    title: 'Organizations & Teams',
    filename: '04-organizations-teams.md',
    description: 'Structure your workspace and manage members effectively',
    duration: '15 min read',
    level: 'Admin',
    order: 4,
  },
  {
    id: 'content-discovery-search',
    title: 'Content Discovery & Search',
    filename: '05-content-discovery-search.md',
    description: 'Find exactly what you need with filters and saved searches',
    duration: '15 min read',
    level: 'All Levels',
    order: 5,
  },
  {
    id: 'content-sharing-public-links',
    title: 'Content Sharing & Public Links',
    filename: '06-content-sharing-public-links.md',
    description: 'Share content securely with password protection and expiration',
    duration: '15 min read',
    level: 'Intermediate',
    order: 6,
  },
  {
    id: 'views-page-builder',
    title: 'Views & Page Builder',
    filename: '07-views-page-builder.md',
    description: 'Create custom pages with the drag-and-drop page builder',
    duration: '25 min read',
    level: 'Advanced',
    order: 7,
  },
  {
    id: 'subscriptions-billing',
    title: 'Subscriptions & Billing',
    filename: '08-subscriptions-billing.md',
    description: 'Understand concurrent user pricing and manage subscriptions',
    duration: '20 min read',
    level: 'Admin',
    order: 8,
  },
  {
    id: 'roles-permissions-rbac',
    title: 'Roles & Permissions (RBAC)',
    filename: '09-roles-permissions-rbac.md',
    description: 'Control access with roles and custom permissions',
    duration: '20 min read',
    level: 'Admin',
    order: 9,
  },
  {
    id: 'profile-account-management',
    title: 'Profile & Account Management',
    filename: '10-profile-account-management.md',
    description: 'Manage your profile, preferences, and account settings',
    duration: '10 min read',
    level: 'All Levels',
    order: 10,
  },
  {
    id: 'file-viewer',
    title: 'File Viewer',
    filename: '11-file-viewer.md',
    description: 'View PDFs, videos, images, and code files in your browser',
    duration: '10 min read',
    level: 'All Levels',
    order: 11,
  },
  {
    id: 'tag-management',
    title: 'Tag Management',
    filename: '12-tag-management.md',
    description: 'Organize content with tags and tag groups',
    duration: '15 min read',
    level: 'Intermediate',
    order: 12,
  },
  {
    id: 'invitations',
    title: 'Invitation Management',
    filename: '13-invitations.md',
    description: 'Send, receive, and manage team invitations',
    duration: '10 min read',
    level: 'All Levels',
    order: 13,
  },
  {
    id: 'ratings-reviews',
    title: 'Content Ratings & Reviews',
    filename: '14-ratings-reviews.md',
    description: 'Rate content and write reviews for quality feedback',
    duration: '10 min read',
    level: 'All Levels',
    order: 14,
  },
  {
    id: 'faq-troubleshooting',
    title: 'FAQ & Troubleshooting',
    filename: '15-faq-troubleshooting.md',
    description: 'Common questions and solutions for all features',
    duration: 'Reference',
    level: 'All Levels',
    order: 15,
  },
];

const GuideViewer: React.FC = () => {
  const theme = useTheme();
  const { guideId } = useParams<{ guideId: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentGuide = guides.find((g) => g.id === guideId);
  const currentIndex = guides.findIndex((g) => g.id === guideId);
  const previousGuide = currentIndex > 0 ? guides[currentIndex - 1] : null;
  const nextGuide = currentIndex < guides.length - 1 ? guides[currentIndex + 1] : null;

  useEffect(() => {
    const loadGuide = async () => {
      if (!currentGuide) {
        setError('Guide not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch the markdown file from the docs folder
        const response = await fetch(`/docs/user-guides/${currentGuide.filename}`);

        if (!response.ok) {
          throw new Error('Failed to load guide');
        }

        const markdown = await response.text();
        const html = await Promise.resolve(marked(markdown));
        setContent(html);
      } catch (err) {
        console.error('Error loading guide:', err);
        setError('Failed to load guide content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadGuide();
  }, [guideId, currentGuide]);

  const handleDownload = () => {
    if (!currentGuide) return;

    // Create a downloadable markdown file
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = currentGuide.filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (!currentGuide) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">Guide not found</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/help')}
          sx={{ mt: 2 }}
        >
          Back to Help Center
        </Button>
      </Container>
    );
  }

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 2 }}>
            <MuiLink
              component="button"
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <Home sx={{ mr: 0.5, fontSize: 20 }} />
              Home
            </MuiLink>
            <MuiLink
              component="button"
              onClick={() => navigate('/help')}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
              }}
            >
              Help Center
            </MuiLink>
            <Typography color="text.primary">{currentGuide.title}</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/help')}
              sx={{ mr: 2 }}
              aria-label="Back to help center"
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                {currentGuide.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={currentGuide.level} size="small" color="primary" />
                <Chip label={currentGuide.duration} size="small" variant="outlined" />
              </Box>
            </Box>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={handleDownload}
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              Download
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {currentGuide.description}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : (
          <Paper sx={{ p: { xs: 3, md: 5 } }}>
            <Box
              className="markdown-content"
              sx={{
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 700,
                  mt: 4,
                  mb: 2,
                  color: 'text.primary',
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  pb: 1,
                  '&:first-of-type': { mt: 0 },
                },
                '& h2': {
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  mt: 4,
                  mb: 2,
                  color: 'text.primary',
                },
                '& h3': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  mt: 3,
                  mb: 1.5,
                  color: 'text.primary',
                },
                '& h4': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  mt: 2,
                  mb: 1,
                  color: 'text.primary',
                },
                '& p': {
                  mb: 2,
                  lineHeight: 1.7,
                  color: 'text.secondary',
                },
                '& ul, & ol': {
                  mb: 2,
                  pl: 3,
                  '& li': {
                    mb: 0.5,
                    lineHeight: 1.7,
                    color: 'text.secondary',
                  },
                },
                '& code': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  mb: 2,
                  '& code': {
                    backgroundColor: 'transparent',
                    padding: 0,
                  },
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  pl: 2,
                  ml: 0,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  mb: 2,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  '& th': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    padding: 1.5,
                    textAlign: 'left',
                    fontWeight: 600,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  },
                  '& td': {
                    padding: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  my: 2,
                },
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& hr': {
                  border: 'none',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  my: 3,
                },
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </Paper>
        )}

        {/* Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 4,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {previousGuide ? (
            <Button
              startIcon={<NavigateBefore />}
              variant="outlined"
              onClick={() => navigate(`/help/guide/${previousGuide.id}`)}
              sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" display="block">
                  Previous
                </Typography>
                <Typography variant="body2">{previousGuide.title}</Typography>
              </Box>
            </Button>
          ) : (
            <Box />
          )}

          {nextGuide && (
            <Button
              endIcon={<NavigateNext />}
              variant="outlined"
              onClick={() => navigate(`/help/guide/${nextGuide.id}`)}
              sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, ml: 'auto' }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" display="block">
                  Next
                </Typography>
                <Typography variant="body2">{nextGuide.title}</Typography>
              </Box>
            </Button>
          )}
        </Box>
      </Container>
    </>
  );
};

export default GuideViewer;
export { guides };
